import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const KIND_META = {
  browser_launch:     { color: "text-amber-300",   label: "Browser launch" },
  software_install:   { color: "text-emerald-300", label: "Software install" },
  software_uninstall: { color: "text-emerald-400", label: "Software uninstall" },
  software_install_attempt: { color: "text-amber-400", label: "Installer launched" },
  file_deleted:       { color: "text-red-300",     label: "File deleted" },
  usb_attach:         { color: "text-blue-300",    label: "USB attached" },
  usb_detach:         { color: "text-blue-400",    label: "USB removed" },
};

const SEVERITY_DOT = {
  info: "bg-zinc-500",
  warn: "bg-amber-400",
  crit: "bg-red-500",
};

function describe(e) {
  const d = e.data || {};
  switch (e.kind) {
    case "browser_launch":           return `${d.process_name || "?"} (pid ${d.pid})`;
    case "software_install":         return `${d.name}${d.version ? ` v${d.version}` : ""}${d.publisher ? ` — ${d.publisher}` : ""}`;
    case "software_uninstall":       return `Removed: ${d.name}`;
    case "software_install_attempt": return `Installer started: ${d.process_name} (pid ${d.pid})`;
    case "file_deleted":             return `${d.basename || d.path || "?"}${d.size_bytes ? ` · ${(d.size_bytes/1024).toFixed(1)} KB` : ""}`;
    case "usb_attach":               return `Drive ${d.device_id} · ${d.volume_name || ""} · serial ${d.serial || "?"}${d.size_bytes ? ` · ${(d.size_bytes/1e9).toFixed(1)} GB` : ""}`;
    case "usb_detach":               return d.key || "?";
    default: return JSON.stringify(d);
  }
}

function fmtTs(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleString();
}

export default function EndpointMonitor() {
  const [devices, setDevices] = useState([]);
  const [events, setEvents] = useState({ items: [], total: 0 });
  const [deviceFilter, setDeviceFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [screenshotId, setScreenshotId] = useState(null);

  const loadDevices = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/agent/devices");
      setDevices(data);
    } catch { /* silent */ }
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      const params = { limit: 200 };
      if (deviceFilter) params.device_id = deviceFilter;
      if (kindFilter) params.kind = kindFilter;
      const { data } = await api.get("/admin/agent/events", { params });
      setEvents(data);
    } catch { setEvents({ items: [], total: 0 }); }
  }, [deviceFilter, kindFilter]);

  useEffect(() => {
    loadDevices();
    loadEvents();
    const t = setInterval(() => { loadDevices(); loadEvents(); }, 20_000);
    return () => clearInterval(t);
  }, [loadDevices, loadEvents]);

  const deleteDevice = async (d) => {
    if (!confirm(`Delete device "${d.display_name || d.hostname || d.id}" and all its events?`)) return;
    await api.delete(`/admin/agent/devices/${d.id}`);
    loadDevices(); loadEvents();
  };

  const [renamingId, setRenamingId] = useState(null);
  const [renameInput, setRenameInput] = useState("");
  const beginRename = (d) => {
    setRenamingId(d.id);
    setRenameInput(d.display_name || "");
  };
  const saveRename = async (d) => {
    const name = (renameInput || "").trim();
    if (name === (d.display_name || "")) { setRenamingId(null); return; }
    try {
      await api.patch(`/admin/agent/devices/${d.id}`, { display_name: name });
      setRenamingId(null);
      loadDevices();
    } catch { /* keep editor open on error */ }
  };
  const cancelRename = () => { setRenamingId(null); setRenameInput(""); };

  const onlineCutoff = Date.now() - 3 * 60 * 1000;

  return (
    <div className="p-10 max-w-7xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-widest text-emerald-400/80">ENDPOINT MONITORING</div>
          <h1 className="text-2xl font-semibold mt-1">Devices & Events</h1>
          <div className="text-xs text-zinc-500 mt-1">v2.1 agent · watchdog-protected · refreshes every 20s</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a
            data-testid="download-em-installer"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/download/endpoint-monitor-installer`}
            className="px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-zinc-900 text-sm font-medium"
          >
            ↓ One-click installer (.exe)
          </a>
          <a
            data-testid="download-em-installer-zip"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/download/endpoint-monitor-installer-zip`}
            className="px-4 py-2 rounded-md border border-emerald-500/40 hover:border-emerald-400 text-sm text-emerald-300"
            title="If your browser blocks the .exe with 'Virus detected', download this .zip and extract."
          >
            ↓ .exe in .zip
          </a>
          <a
            data-testid="download-em-bare"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/download/endpoint-monitor`}
            className="px-4 py-2 rounded-md border border-emerald-500/40 hover:border-emerald-400 text-sm text-emerald-300"
          >
            ↓ Bare .exe (GPO)
          </a>
          <a
            data-testid="download-em-bare-zip"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/download/endpoint-monitor-zip`}
            className="px-4 py-2 rounded-md border border-emerald-500/40 hover:border-emerald-400 text-sm text-emerald-300"
            title="Bare .exe zipped — for GPO/RMM push when direct .exe is blocked."
          >
            ↓ Bare in .zip
          </a>
        </div>
      </div>

      {/* Devices list */}
      <div className="mb-10" data-testid="devices-section">
        <div className="text-xs tracking-widest text-zinc-500 uppercase mb-3">Registered devices ({devices.length})</div>
        <div className="rounded-lg border border-zinc-800 bg-[#0e0f14] divide-y divide-zinc-900">
          {devices.length === 0 && (
            <div className="p-5 text-sm text-zinc-500">
              No devices registered yet. Download the installer above and run it on a Windows machine.
            </div>
          )}
          {devices.map((d) => {
            const lastSeen = d.last_seen ? new Date(d.last_seen).getTime() : 0;
            const online = lastSeen > onlineCutoff;
            const isRenaming = renamingId === d.id;
            const primaryLabel = d.display_name || d.hostname || d.id.slice(0, 8);
            return (
              <div key={d.id} className="p-4 flex items-center justify-between text-sm" data-testid={`device-row-${d.id}`}>
                <div className="min-w-0 flex-1 flex items-center gap-3">
                  <span className={`inline-block w-2.5 h-2.5 rounded-full ${online ? "bg-emerald-400 animate-pulse" : "bg-zinc-600"}`} title={online ? "Online" : "Offline (>3 min)"} />
                  <div className="min-w-0 flex-1">
                    {isRenaming ? (
                      <div className="flex items-center gap-2">
                        <input
                          autoFocus
                          value={renameInput}
                          onChange={(e) => setRenameInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveRename(d);
                            if (e.key === "Escape") cancelRename();
                          }}
                          maxLength={80}
                          placeholder="e.g. Riya — Marketing — Floor 3"
                          className="bg-zinc-950 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-100 focus:outline-none focus:border-emerald-500 w-72"
                          data-testid={`device-rename-input-${d.id}`}
                        />
                        <button onClick={() => saveRename(d)} className="text-xs px-2 py-1 rounded bg-emerald-500 text-zinc-900 hover:bg-emerald-400" data-testid={`device-rename-save-${d.id}`}>Save</button>
                        <button onClick={cancelRename} className="text-xs px-2 py-1 rounded text-zinc-400 hover:text-zinc-200" data-testid={`device-rename-cancel-${d.id}`}>Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button onClick={() => setDeviceFilter(deviceFilter === d.id ? "" : d.id)}
                          className={`text-zinc-100 hover:text-emerald-300 truncate text-left ${deviceFilter === d.id ? "text-emerald-300" : ""}`}
                          data-testid={`device-filter-${d.id}`}>
                          {primaryLabel}
                          {deviceFilter === d.id && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-700/40">FILTERED</span>}
                        </button>
                        <button
                          onClick={() => beginRename(d)}
                          title="Rename device"
                          className="text-zinc-500 hover:text-emerald-400 text-xs"
                          data-testid={`device-rename-${d.id}`}
                        >✎</button>
                      </div>
                    )}
                    <div className="text-[11px] text-zinc-500 truncate">
                      {d.display_name && <span className="text-zinc-600">{d.hostname} · </span>}
                      {d.os}/{d.arch} · agent v{d.agent_ver || "?"} · {d.event_count || 0} events · last IP {d.last_ip || "?"}
                    </div>
                  </div>
                </div>
                <div className="text-right shrink-0 ml-4 flex items-center gap-3">
                  <div>
                    <div className="text-xs text-zinc-300">{relTime(d.last_seen)}</div>
                    <div className="text-[11px] text-zinc-500">last seen</div>
                  </div>
                  <button onClick={() => deleteDevice(d)} data-testid={`device-delete-${d.id}`}
                    className="text-xs text-red-400 px-2 py-1 border border-red-900/60 rounded hover:bg-red-900/20">
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Events list */}
      <div data-testid="events-section">
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="text-xs tracking-widest text-zinc-500 uppercase">Events ({events.total})</div>
            {(deviceFilter || kindFilter) && (
              <button onClick={() => { setDeviceFilter(""); setKindFilter(""); }} className="text-[11px] text-emerald-300 hover:text-emerald-200 mt-1" data-testid="clear-filters">
                Clear filters
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}
              data-testid="kind-filter"
              className="text-xs px-2 py-1.5 rounded-md bg-zinc-900 border border-zinc-800">
              <option value="">All kinds</option>
              {Object.entries(KIND_META).map(([k, m]) => (
                <option key={k} value={k}>{m.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="rounded-lg border border-zinc-800 bg-[#0e0f14] divide-y divide-zinc-900">
          {events.items.length === 0 && (
            <div className="p-5 text-sm text-zinc-500">No events match the current filters.</div>
          )}
          {events.items.map((e) => {
            const meta = KIND_META[e.kind] || { color: "text-zinc-300", label: e.kind };
            const dev = devices.find((dd) => dd.id === e.device_id);
            const deviceLabel = (dev && dev.display_name) || e.hostname || e.device_id.slice(0, 8);
            return (
              <div key={e.id} className="p-3 flex items-start gap-3 text-sm" data-testid={`event-row-${e.id}`}>
                <span className={`inline-block w-2 h-2 rounded-full mt-2 ${SEVERITY_DOT[e.severity] || "bg-zinc-500"}`} title={e.severity}/>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs uppercase tracking-wider ${meta.color}`}>{meta.label}</span>
                    <span className="text-[11px] text-zinc-500">{fmtTs(e.ts)}</span>
                    <span className="text-[11px] text-zinc-600">· {deviceLabel}</span>
                  </div>
                  <div className="text-zinc-300 mt-0.5 truncate">{describe(e)}</div>
                </div>
                {e.screenshot_id && (
                  <button onClick={() => setScreenshotId(e.screenshot_id)}
                    data-testid={`view-screenshot-${e.id}`}
                    className="text-[11px] px-2 py-1 rounded border border-emerald-700/50 text-emerald-300 hover:bg-emerald-900/20 shrink-0">
                    📷 view
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Screenshot modal */}
      {screenshotId && (
        <div onClick={() => setScreenshotId(null)}
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6"
          data-testid="screenshot-modal">
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/admin/agent/screenshots/${screenshotId}`}
            alt="screenshot"
            className="max-w-full max-h-full border border-zinc-700 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button onClick={() => setScreenshotId(null)}
            className="absolute top-6 right-6 text-zinc-300 hover:text-white text-3xl">×</button>
        </div>
      )}
    </div>
  );
}
